export const REPROCESSABLE_STATUSES = new Set([
  'PROCESS_COMPLETED',
  'PROCESS_PARTIALLY_COMPLETED',
  'DUPLICATE_SKIPPED',
]);

export const EDITOR_ROLES = new Set(['admin', 'editor']);

const LIST_REPROCESSABLE_DOC_TYPES = `
  query ListReprocessableDocTypes {
    listReprocessableDocTypes {
      items {
        document_type
        enabled
      }
    }
  }
`;

const LIST_PROMPT_TEMPLATES_FOR_REPROCESS = `
  query ListPromptTemplatesForReprocess {
    listPromptTemplates(orderBy: "document_type") {
      items {
        document_type
        enabled
      }
    }
  }
`;

const MY_TENANT_ROLE_QUERY = `
  query MyTenantRoleForReprocess {
    myTenant {
      my_role
    }
  }
`;

const EXCLUDED_REPROCESS_TYPES = new Set(['', 'unclassified', 'hidden']);

const REPROCESS_TYPE_QUERIES = [
  {
    query: LIST_REPROCESSABLE_DOC_TYPES,
    pick: (res) => res?.listReprocessableDocTypes?.items,
  },
  {
    query: LIST_PROMPT_TEMPLATES_FOR_REPROCESS,
    pick: (res) => res?.listPromptTemplates?.items,
  },
];

function isPromptEnabled(enabled) {
  if (enabled == null) return true;
  if (typeof enabled === 'boolean') return enabled;
  const value = String(enabled).toLowerCase();
  return value !== '0' && value !== 'false';
}

export function normalizeReprocessDocTypes(items) {
  const byType = new Map();
  (items || []).forEach((item) => {
    const documentType = (item?.document_type || '').trim();
    const key = documentType.toLowerCase();
    if (!documentType || EXCLUDED_REPROCESS_TYPES.has(key)) return;
    if (!isPromptEnabled(item.enabled)) return;
    if (!byType.has(key)) {
      byType.set(key, { document_type: documentType });
    }
  });
  return Array.from(byType.values()).sort((a, b) =>
    (a.document_type || '').localeCompare(b.document_type || '')
  );
}

export function resolveReprocessRole(claims) {
  const explicit = (claims?.['custom:role'] || claims?.role || '').toLowerCase();
  if (explicit && !['none', 'pending'].includes(explicit)) {
    return explicit;
  }

  const tenantId = claims?.['custom:tenant_id'];
  if (tenantId && tenantId !== 'pending') {
    return '';
  }

  return 'reader';
}

export function canUserReprocess(claims) {
  return EDITOR_ROLES.has(resolveReprocessRole(claims));
}

export async function resolveCanUserReprocess({ claims, callApi }) {
  if (canUserReprocess(claims)) {
    return true;
  }

  if (!callApi) {
    return false;
  }

  try {
    const res = await callApi(MY_TENANT_ROLE_QUERY);
    const role = (res?.myTenant?.my_role || '').toLowerCase();
    return EDITOR_ROLES.has(role);
  } catch (err) {
    console.warn('Could not resolve reprocess role from myTenant:', err);
    return false;
  }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function loadReprocessDocTypes(callApi, { retries = 2 } = {}) {
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    for (const { query, pick } of REPROCESS_TYPE_QUERIES) {
      try {
        const res = await callApi(query);
        const types = normalizeReprocessDocTypes(pick(res));
        if (types.length) {
          return types;
        }
      } catch (err) {
        console.warn('Failed to load reprocess doc types:', err);
      }
    }

    if (attempt < retries) {
      await sleep(400 * (attempt + 1));
    }
  }

  return [];
}

export async function loadReprocessContext(callApi, getClaims) {
  await refreshSessionSafe();

  let claims = {};
  try {
    claims = typeof getClaims === 'function' ? (await getClaims()) || {} : {};
  } catch (err) {
    console.warn('Could not read user claims for reprocess menu:', err);
  }

  const canEdit = await resolveCanUserReprocess({ claims, callApi });
  const types = canEdit ? await loadReprocessDocTypes(callApi) : [];

  return { canEdit, reprocessDocTypes: types };
}

async function refreshSessionSafe() {
  try {
    const { refreshSessionIfTenantPending } = await import('../common/auth');
    await refreshSessionIfTenantPending();
  } catch {
    // Sin sesión activa — el menú quedará deshabilitado.
  }
}

export function isReprocessableDocument(row, { canEdit = false, reprocessDocTypes = [] } = {}) {
  if (!row || !canEdit || !reprocessDocTypes.length) {
    return false;
  }

  const docType = (row.doc_type || row.type || '').toLowerCase();
  const status = (row.status || '').toUpperCase();

  return (
    docType === 'unclassified' &&
    !row.hidden_reason &&
    REPROCESSABLE_STATUSES.has(status)
  );
}

export function getReprocessUnavailableReason({ row, canEdit, reprocessDocTypes }) {
  if (!row) return null;
  const docType = (row.doc_type || row.type || '').toLowerCase();
  const status = (row.status || '').toUpperCase();

  if (docType !== 'unclassified' || row.hidden_reason) {
    return null;
  }
  if (!REPROCESSABLE_STATUSES.has(status)) {
    return null;
  }
  if (!canEdit) {
    return 'Requiere rol editor o admin';
  }
  if (!reprocessDocTypes.length) {
    return 'Sin tipos destino configurados';
  }
  return null;
}
